import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as api from '../utils/api'
import { uploadCover } from '../utils/media'
import EventCard from '../Components/EventCard'
import { C, gradient, fontStack, btn, input, card } from '../constant/theme'

export default function GroupEditor() {
  const { groupId } = useParams()
  const navigate = useNavigate()
  const [group, setGroup] = useState(null)
  const [days, setDays] = useState([])
  const [events, setEvents] = useState([])
  const [media, setMedia] = useState([])
  const [members, setMembers] = useState([]) // [{ email, name }]
  const [loading, setLoading] = useState(true)
  const [savingCover, setSavingCover] = useState(false)
  const [tab, setTab] = useState('timeline') // 'timeline' | 'settings'
  const [mobile, setMobile] = useState(window.innerWidth < 640)

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      const data = await api.getGroup(groupId)
      setGroup(data.group)
      setDays(data.days)
      setEvents(data.events)
      setMedia(data.media)
      setMembers(data.members || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId])

  // ── Days ──
  const addDay = async () => {
    const date = new Date().toISOString().slice(0, 10)
    const day = await api.createDay(groupId, { date, title: `Day ${days.length + 1}` })
    setDays((d) => [...d, day])
  }
  const saveDay = async (dayId, patch) => {
    const day = await api.updateDay(groupId, dayId, patch)
    setDays((d) => d.map((x) => (x._id === dayId ? day : x)))
  }
  const removeDay = async (dayId) => {
    if (!window.confirm('Delete this day and all its events & media?')) return
    await api.deleteDay(groupId, dayId)
    setDays((d) => d.filter((x) => x._id !== dayId))
    setEvents((e) => e.filter((x) => x.dayId !== dayId))
    setMedia((m) => m.filter((x) => events.find((e) => e._id === x.eventId)?.dayId !== dayId))
  }

  // ── Events ──
  const addEvent = async (dayId) => {
    const event = await api.createEvent(groupId, dayId, { title: 'New event' })
    setEvents((e) => [...e, event])
  }
  const saveEvent = async (eventId, patch) => {
    const event = await api.updateEvent(groupId, eventId, patch)
    setEvents((e) => e.map((x) => (x._id === eventId ? event : x)))
  }
  const removeEvent = async (eventId) => {
    if (!window.confirm('Delete this event and its media?')) return
    await api.deleteEvent(groupId, eventId)
    setEvents((e) => e.filter((x) => x._id !== eventId))
    setMedia((m) => m.filter((x) => x.eventId !== eventId))
  }

  // ── Media (callbacks bubbled up from EventCard) ──
  const onMediaAdded = (m) => setMedia((prev) => [...prev, m])
  const onMediaRemoved = (mediaId) => setMedia((prev) => prev.filter((x) => x._id !== mediaId))
  const onMediaUpdated = (m) => setMedia((prev) => prev.map((x) => (x._id === m._id ? m : x)))

  // ── Cover ──
  const onCover = async (file) => {
    if (!file) return
    setSavingCover(true)
    try {
      const coverImage = await uploadCover({ groupId, file })
      const updated = await api.updateGroup(groupId, { coverImage })
      setGroup(updated)
    } finally {
      setSavingCover(false)
    }
  }

  if (loading || !group) return <div style={{ ...S.root, padding: 40, fontFamily: fontStack }}>Loading…</div>

  return (
    <div style={S.root}>
      <header style={{ ...S.header, padding: mobile ? '12px 14px' : '14px 24px' }}>
        <button style={btn('ghost')} onClick={() => navigate('/admin')}>← Trips</button>
        <strong style={{ color: C.text, fontSize: mobile ? 14 : 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1, textAlign: 'center' }}>{group.name}</strong>
        <a href={`/g/${group.slug}`} target="_blank" rel="noreferrer" style={S.viewLink}>View ↗</a>
      </header>

      <div style={{ ...S.tabs, padding: mobile ? '0 14px' : '0 24px' }}>
        <button style={tabBtn(tab === 'timeline')} onClick={() => setTab('timeline')}>Timeline</button>
        <button style={tabBtn(tab === 'settings')} onClick={() => setTab('settings')}>Trip settings</button>
      </div>

      <main style={{ ...S.main, padding: mobile ? '16px 12px' : '24px' }}>
        {tab === 'settings' ? (
          <Settings group={group} groupId={groupId} setGroup={setGroup} onCover={onCover} savingCover={savingCover} navigate={navigate} members={members} setMembers={setMembers} mobile={mobile} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {days.length === 0 && <div style={{ ...card, color: C.textSoft }}>No days yet. Add your first day to start the timeline.</div>}
            {days
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((day) => (
                <DaySection
                  key={day._id}
                  day={day}
                  events={events.filter((e) => e.dayId === day._id)}
                  media={media}
                  members={members}
                  groupId={groupId}
                  onSaveDay={saveDay}
                  onRemoveDay={removeDay}
                  onAddEvent={addEvent}
                  onSaveEvent={saveEvent}
                  onRemoveEvent={removeEvent}
                  onMediaAdded={onMediaAdded}
                  onMediaRemoved={onMediaRemoved}
                  onMediaUpdated={onMediaUpdated}
                />
              ))}
            <button style={{ ...btn('primary'), alignSelf: 'flex-start' }} onClick={addDay}>+ Add day</button>
          </div>
        )}
      </main>
    </div>
  )
}

function DaySection({ day, events, media, members, groupId, onSaveDay, onRemoveDay, onAddEvent, onSaveEvent, onRemoveEvent, onMediaAdded, onMediaRemoved, onMediaUpdated }) {
  const [title, setTitle] = useState(day.title)
  const [date, setDate] = useState(day.date)
  const dayMembers = day.members || []

  const toggleMember = (email) => {
    const next = dayMembers.includes(email) ? dayMembers.filter((e) => e !== email) : [...dayMembers, email]
    onSaveDay(day._id, { members: next })
  }

  return (
    <section style={S.day}>
      <div style={S.dayHead}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 140 }}>
          <label style={S.fieldLabel}>Day title</label>
          <input style={{ ...input, fontWeight: 700, width: '100%' }} value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => onSaveDay(day._id, { title })} placeholder="e.g. Day 1 — Arrival" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 150, flexShrink: 0 }}>
          <label style={S.fieldLabel}>Date</label>
          <input style={{ ...input, width: '100%' }} type="date" value={date} onChange={(e) => { setDate(e.target.value); onSaveDay(day._id, { date: e.target.value }) }} />
        </div>
        <button style={{ ...btn('danger'), alignSelf: 'flex-end' }} onClick={() => onRemoveDay(day._id)}>Delete day</button>
      </div>

      {members.length > 0 && (
        <div style={S.dayMembers}>
          <span style={S.dayMembersLabel}>Who was here (optional):</span>
          {members.map((m) => {
            const on = dayMembers.includes(m.email)
            return (
              <button key={m.email} type="button" style={on ? S.memberChipOn : S.memberChipOff} onClick={() => toggleMember(m.email)}>
                {on ? '✓ ' : ''}{m.name || m.email}
              </button>
            )
          })}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
        {events
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((event) => (
            <EventCard
              key={event._id}
              event={event}
              media={media.filter((m) => m.eventId === event._id)}
              groupId={groupId}
              onSave={onSaveEvent}
              onRemove={onRemoveEvent}
              onMediaAdded={onMediaAdded}
              onMediaRemoved={onMediaRemoved}
              onMediaUpdated={onMediaUpdated}
            />
          ))}
        <button style={{ ...btn('ghost'), alignSelf: 'flex-start' }} onClick={() => onAddEvent(day._id)}>+ Add event</button>
      </div>
    </section>
  )
}

function Settings({ group, groupId, setGroup, onCover, savingCover, navigate, members, setMembers, mobile }) {
  const [form, setForm] = useState({
    name: group.name, summary: group.summary, locationName: group.locationName,
    startDate: group.startDate, endDate: group.endDate,
  })
  const [saving, setSaving] = useState(false)
  const [memberEmail, setMemberEmail] = useState('')
  const [memberErr, setMemberErr] = useState('')
  const [addingMember, setAddingMember] = useState(false)
  const [allUsers, setAllUsers] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  useEffect(() => {
    api.listUsers().then(setAllUsers).catch(() => {})
  }, [])

  const memberEmails = new Set(members.map((m) => m.email))
  const suggestions = allUsers.filter((u) => {
    if (memberEmails.has(u.email)) return false
    const q = memberEmail.trim().toLowerCase()
    if (!q) return true
    return u.email.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q)
  }).slice(0, 6)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const doAddMember = async (email) => {
    if (!email.trim()) return
    setMemberErr('')
    setAddingMember(true)
    setShowSuggestions(false)
    try {
      setMembers(await api.addMember(groupId, email.trim()))
      setMemberEmail('')
    } catch (err) {
      setMemberErr(err?.response?.data?.message || 'Could not add member.')
    } finally {
      setAddingMember(false)
    }
  }

  const addMember = async (e) => {
    e.preventDefault()
    doAddMember(memberEmail)
  }

  const removeMember = async (email) => {
    setMembers(await api.removeMember(groupId, email))
  }

  const save = async () => {
    setSaving(true)
    try {
      const updated = await api.updateGroup(groupId, { ...form })
      setGroup(updated)
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!window.confirm('Permanently delete this entire trip, all media and content?')) return
    await api.deleteGroup(groupId)
    navigate('/admin')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 620 }}>
      <div style={card}>
        <h3 style={S.h3}>Cover image</h3>
        <div style={{ ...S.coverPreview, backgroundImage: group.coverImage?.url ? `url(${group.coverImage.url})` : gradient }} />
        <label style={{ ...btn('ghost'), display: 'inline-block', marginTop: 12 }}>
          {savingCover ? 'Uploading…' : 'Upload cover'}
          <input type="file" accept="image/*" hidden onChange={(e) => onCover(e.target.files[0])} />
        </label>
      </div>

      <div style={card}>
        <h3 style={S.h3}>Trip details</h3>
        <Field label="Name"><input style={input} value={form.name} onChange={set('name')} /></Field>
        <Field label="Summary"><textarea style={{ ...input, minHeight: 80, resize: 'vertical' }} value={form.summary} onChange={set('summary')} placeholder="A short blurb about the trip" /></Field>
        <Field label="Location"><input style={input} value={form.locationName} onChange={set('locationName')} placeholder="e.g. Goa, India" /></Field>
        <div style={{ display: 'flex', gap: 12, flexWrap: mobile ? 'wrap' : 'nowrap' }}>
          <Field label="Start date"><input style={input} type="date" value={form.startDate} onChange={set('startDate')} /></Field>
          <Field label="End date"><input style={input} type="date" value={form.endDate} onChange={set('endDate')} /></Field>
        </div>
      </div>

      <div style={card}>
        <h3 style={S.h3}>Members</h3>
        <p style={{ fontSize: 13, color: C.textSoft, marginTop: 0 }}>
          Add people by the email of their existing account. Members can sign in and
          upload photos to this trip (but can't edit it). Names are fetched automatically.
        </p>
        <form onSubmit={addMember} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200, position: 'relative', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={S.fieldLabel}>Search member</label>
            <input
              style={{ ...input, width: '100%', boxSizing: 'border-box' }}
              type="text"
              value={memberEmail}
              onChange={(e) => { setMemberEmail(e.target.value); setShowSuggestions(true) }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="Name or email…"
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div style={S.suggestionBox}>
                {suggestions.map((u) => (
                  <div
                    key={u.email}
                    style={S.suggestionItem}
                    onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                    onMouseDown={() => doAddMember(u.email)}
                  >
                    <span style={{ fontWeight: 600, color: C.text }}>{u.name || '(no name)'}</span>
                    <span style={{ color: C.textSoft, fontSize: 12 }}>{u.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button style={btn('ghost')} type="submit" disabled={addingMember}>{addingMember ? 'Adding…' : 'Add member'}</button>
        </form>
        {memberErr && <div style={S.memberErr}>⚠️ {memberErr}</div>}
        {members.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {members.map((m) => (
              <div key={m.email} style={S.memberRow}>
                <div style={{ minWidth: 0 }}>
                  <div style={S.memberName}>{m.name || <span style={{ color: C.muted }}>(no account)</span>}</div>
                  <div style={S.memberEmail}>{m.email}</div>
                </div>
                <button style={btn('danger')} onClick={() => removeMember(m.email)}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button style={btn('primary')} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        <button style={btn('danger')} onClick={remove}>Delete trip</button>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, flex: 1 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#2e3d52' }}>{label}</label>
      {children}
    </div>
  )
}

const tabBtn = (active) => ({
  padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
  fontSize: 14, fontWeight: 700, color: active ? C.navy2 : C.muted,
  borderBottom: active ? `2px solid ${C.navy2}` : '2px solid transparent',
})

const S = {
  root: { minHeight: '100vh', background: C.bg, fontFamily: fontStack },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 24px', background: '#fff', borderBottom: `1px solid ${C.cardBorder}` },
  viewLink: { fontSize: 13, color: C.navy2, textDecoration: 'none', fontWeight: 600 },
  tabs: { display: 'flex', gap: 6, padding: '0 24px', background: '#fff', borderBottom: `1px solid ${C.cardBorder}` },
  main: { maxWidth: 920, margin: '0 auto', padding: '24px' },
  day: { background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 18 },
  dayHead: { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: '#2e3d52' },
  dayMembers: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 },
  dayMembersLabel: { fontSize: 12.5, color: C.textSoft, fontWeight: 600, marginRight: 2 },
  memberChipOn: { fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, border: `1px solid ${C.navy2}`, background: C.navy2, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  memberChipOff: { fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, border: `1px solid ${C.inputBorder}`, background: 'transparent', color: C.textSoft, cursor: 'pointer', fontFamily: 'inherit' },
  memberRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderTop: `1px solid ${C.cardBorder}` },
  memberName: { fontSize: 14, fontWeight: 600, color: C.text },
  memberEmail: { fontSize: 12.5, color: C.textSoft },
  memberErr: { background: C.dangerBg, color: C.danger, borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8 },
  suggestionBox: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: `1px solid ${C.cardBorder}`, borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', marginTop: 4, overflow: 'hidden' },
  suggestionItem: { display: 'flex', flexDirection: 'column', gap: 1, padding: '9px 14px', cursor: 'pointer', background: '#fff', borderBottom: `1px solid ${C.cardBorder}` },
  h3: { margin: '0 0 12px', fontSize: 15, color: C.text },
  coverPreview: { height: 150, borderRadius: 12, backgroundSize: 'cover', backgroundPosition: 'center' },
}
